import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSiteLang } from "../lib/siteLang";

export const Navbar = ({ minimal = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useSiteLang();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  const links = [
    { id: "how-it-works", label: t.navHow },
    { id: "tours", label: t.navTours },
    { id: "features", label: t.navFeatures },
  ];

  const langToggle = (
    <div className="flex items-center rounded-full border border-white/20 p-0.5 text-xs font-semibold" role="group" aria-label="Language">
      {["it", "en"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          aria-pressed={lang === code}
          className={`px-3.5 py-3 rounded-full uppercase transition-colors ${lang === code ? "bg-white text-[#09101F]" : "text-white/70 hover:text-white"}`}
          data-testid={`lang-${code}`}
        >
          {code}
        </button>
      ))}
    </div>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? "glass-nav" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src="/images/logo-192.png" alt="GozzoGuide" className="h-10 w-10 rounded-xl object-cover" />
            <span className="font-bold text-lg text-[#FF6B00] tracking-tight">GozzoGuide</span>
          </Link>

          <div className="flex items-center gap-3 md:gap-7">
            {!minimal &&
              links.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="hidden md:inline text-sm font-medium text-white/80 hover:text-white transition-colors"
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            {/* The language switch stays in the bar on every screen: a visitor must find it at once */}
            {!minimal && langToggle}
            {!minimal && (
              <button onClick={() => scrollToSection("download")} className="hidden md:inline-block btn-primary text-sm !py-2 !px-5" data-testid="nav-download-btn">
                {t.navDownload}
              </button>
            )}
            {!minimal && (
              <button
                className="md:hidden p-2 -mr-2 text-white"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-label={t.menu}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>
        </div>

        {isMobileMenuOpen && !minimal && (
          <div className="md:hidden pb-6 flex flex-col gap-1">
            {links.map((link) => (
              <button key={link.id} onClick={() => scrollToSection(link.id)} className="text-base font-medium text-white py-3 text-left border-b border-white/10">
                {link.label}
              </button>
            ))}
            <button onClick={() => scrollToSection("download")} className="btn-primary text-base mt-4 w-full">
              {t.navDownload}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
